import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ShoppingCart,
  Loader2,
  Check,
  Plus,
  Minus,
  AlertCircle,
  Zap,
  LogOut,
  Award,
  Clock,
  Palette,
  CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/useAuth';
import { api, SKU, PricingQuote, Flag } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { displayCredits } from '@/lib/priceConverter';
import { trackEvent, trackPageView } from '@/lib/analytics';
import { savePendingTikTokPurchase, trackTikTokFunnelEvent } from '@/lib/tiktok';

const vectorCategories = [
  { id: 'credits', name: 'Credits', code: 'CREDITS' },
  { id: 'v1', name: 'Images', code: 'V1' },
  { id: 'v3', name: 'Videos', code: 'V3' },
  { id: 'v4', name: 'Voice Clone', code: 'V4' },
  { id: 'v5', name: 'Voiceover', code: 'V5' },
  { id: 'v6', name: 'SEO Content', code: 'V6' },
  { id: 'v7', name: 'Bundles', code: 'V7' },
];

export default function CreatePage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check for SKU in URL params
  const urlParams = new URLSearchParams(window.location.search);
  const initialSKU = urlParams.get('sku');

  const [selectedCategory, setSelectedCategory] = useState(initialSKU === 'STARTER-50' ? 'credits' : 'v1');
  const selectedCategoryRef = useRef(selectedCategory);
  const [skus, setSKUs] = useState<SKU[]>([]);
  const [selectedSKU, setSelectedSKU] = useState<SKU | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [quote, setQuote] = useState<PricingQuote | null>(null);
  const [isLoadingSKUs, setIsLoadingSKUs] = useState(true);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [flags, setFlags] = useState<Flag[]>([]);
  const [selectedFlags, setSelectedFlags] = useState<string[]>([]);
  const [isLoadingFlags, setIsLoadingFlags] = useState(true);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);

  useEffect(() => {
    trackPageView('create', { initialSku: initialSKU || null });
  }, [initialSKU]);

  useEffect(() => {
    selectedCategoryRef.current = selectedCategory;
  }, [selectedCategory]);

  const loadFlags = useCallback(async () => {
    setIsLoadingFlags(true);
    const result = await api.getFlags();

    if (result.success && result.data) {
      setFlags(result.data);
    } else {
      toast({
        title: t('createPage.loadPricingFailed'),
        description: result.error || t('createPage.tryLater'),
        variant: 'destructive',
      });
    }

    setIsLoadingFlags(false);
  }, [toast]);

  const loadSKUs = useCallback(async (vectorId: string) => {
    setIsLoadingSKUs(true);
    console.log('[CreatePage] Loading SKUs', { vectorId, initialSKU });

    // If we have an initial SKU from URL, load all SKUs first to find it
    if (initialSKU) {
      console.log('[CreatePage] Initial SKU present, loading all SKUs to find it');
      const allResult = await api.getSKUs();
      console.log('[CreatePage] All SKUs loaded', { success: allResult.success, dataLength: allResult.data?.length });

      if (allResult.success && allResult.data) {
        const foundSKU = allResult.data.find(sku => sku.code === initialSKU);
        console.log('[CreatePage] Searching for initial SKU', {
          initialSKU,
          found: !!foundSKU,
          availableCodes: allResult.data.map(s => `${s.code}(${s.vectorId})`)
        });

        if (foundSKU) {
          console.log('[CreatePage] Found SKU, setting category and selected', {
            code: foundSKU.code,
            vectorId: foundSKU.vectorId
          });
          setSelectedSKU(foundSKU);
          // Update category to match the found SKU
          setSelectedCategory(foundSKU.vectorId);
          // Now load SKUs for the correct category
          const categoryResult = await api.getSKUs(foundSKU.vectorId);
          if (categoryResult.success && categoryResult.data) {
            setSKUs(categoryResult.data);
          }
          setIsLoadingSKUs(false);
          return;
        } else {
          console.log('[CreatePage] Initial SKU not found in database', {
            initialSKU,
            hint: 'This SKU may not exist or is inactive'
          });
          toast({
            title: t('createPage.skuNotFoundTitle'),
            description: t('createPage.skuNotFoundSystem', { sku: initialSKU }),
            variant: 'destructive',
          });
        }
      }
    }

    // Load SKUs for the specified vectorId
    const result = await api.getSKUs(vectorId);
    console.log('[CreatePage] API getSKUs result', { success: result.success, dataLength: result.data?.length });

    if (result.success && result.data) {
      setSKUs(result.data);

      // Default selection
      if (result.data.length > 0) {
        console.log('[CreatePage] Selecting first SKU in category', {
          firstSKU: result.data[0].code,
          vectorId: result.data[0].vectorId
        });
        setSelectedSKU(result.data[0]);
      }
    } else {
      console.error('[CreatePage] Failed to load SKUs', { error: result.error });
      toast({
        title: t('createPage.loadProductsFailed'),
        description: result.error || t('createPage.tryLater'),
        variant: 'destructive',
      });
    }

    setIsLoadingSKUs(false);
  }, [initialSKU, toast]);

  const loadQuote = useCallback(async (skuCode: string, qty: number, appliedFlags: string[]) => {
    setIsLoadingQuote(true);
    setQuote(null);

    const result = await api.getPricingQuote(skuCode, qty, appliedFlags);

    if (result.success && result.data) {
      setQuote(result.data);
    } else {
      toast({
        title: t('createPage.priceFailed'),
        description: result.error || t('createPage.tryAgain'),
        variant: 'destructive',
      });
    }

    setIsLoadingQuote(false);
  }, [toast]);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  // Handle initial SKU from URL parameter and manual category changes
  useEffect(() => {
    const loadInitialSKU = async () => {
      if (initialSKU) {
        console.log('[CreatePage] Initial SKU effect triggered', { initialSKU });
        setIsLoadingSKUs(true);

        // Load all SKUs to find the initial one
        const allResult = await api.getSKUs();

        if (allResult.success && allResult.data) {
          const foundSKU = allResult.data.find(sku => sku.code === initialSKU);

          if (foundSKU) {
            console.log('[CreatePage] Found initial SKU, setting up', {
              code: foundSKU.code,
              vectorId: foundSKU.vectorId
            });
            setSelectedSKU(foundSKU);
            setSelectedCategory(foundSKU.vectorId);

            // Load SKUs for the correct category
            const categoryResult = await api.getSKUs(foundSKU.vectorId);
            if (categoryResult.success && categoryResult.data) {
              setSKUs(categoryResult.data);
            }
          } else {
            console.log('[CreatePage] Initial SKU not found', { initialSKU });
            toast({
              title: t('createPage.skuNotFoundTitle'),
              description: t('createPage.skuNotFound', { sku: initialSKU }),
              variant: 'destructive',
            });
            loadSKUs(selectedCategoryRef.current);
          }
        } else {
          console.error('[CreatePage] Failed to load all SKUs');
          loadSKUs(selectedCategoryRef.current);
        }

        setIsLoadingSKUs(false);
      } else {
        loadSKUs(selectedCategoryRef.current);
      }
    };

    loadInitialSKU();
  }, [initialSKU, loadSKUs, toast]);

  // Handle manual category changes
  useEffect(() => {
    if (!initialSKU) {
      loadSKUs(selectedCategory);
    }
  }, [selectedCategory, initialSKU, loadSKUs]);

  useEffect(() => {
    if (selectedSKU) {
      loadQuote(selectedSKU.code, quantity, selectedFlags);
    }
  }, [selectedSKU, quantity, selectedFlags, loadQuote]);

  const handleQuantityChange = (delta: number) => {
    const newQty = Math.max(1, Math.min(100, quantity + delta));
    setQuantity(newQty);
  };

  const toggleFlag = (flagCode: string) => {
    setSelectedFlags(prev => {
      if (prev.includes(flagCode)) {
        return prev.filter(f => f !== flagCode);
      } else {
        return [...prev, flagCode];
      }
    });
  };

  const getFlagIcon = (flagCode: string) => {
    if (flagCode === 'R') return <Clock className="w-4 h-4" />;
    if (flagCode === 'C') return <Palette className="w-4 h-4" />;
    if (flagCode.startsWith('L_')) return <Award className="w-4 h-4" />;
    return <Check className="w-4 h-4" />;
  };

  const handlePlaceOrder = async () => {
    const mxnValueFromQuote = Number(quote?.customerPriceCents || 0) / 100;
    trackTikTokFunnelEvent('AddToCart', {
      skuCode: selectedSKU?.code,
      name: selectedSKU?.name,
      valueMxn: mxnValueFromQuote,
    });

    if (!selectedSKU || !quote) {
      console.log('[CreatePage] Cannot proceed - missing SKU or quote', {
        hasSKU: !!selectedSKU,
        hasQuote: !!quote,
        selectedSKU: selectedSKU ? { code: selectedSKU.code, vectorId: selectedSKU.vectorId } : null
      });
      return;
    }

    console.log('[CreatePage] Proceeding to Stripe checkout', {
      skuCode: selectedSKU.code,
      vectorId: selectedSKU.vectorId,
      quantity,
      flags: selectedFlags
    });

    // ALL purchases now go through Stripe checkout
    setIsPlacingOrder(true);
    trackEvent('checkout_started', {
      source: 'create_page',
      skuCode: selectedSKU.code,
      quantity,
      priceCents: quote.customerPriceCents,
      flags: selectedFlags,
    });
    savePendingTikTokPurchase({
      skuCode: selectedSKU.code,
      name: selectedSKU.name,
      valueMxn: mxnValueFromQuote,
    });
    trackTikTokFunnelEvent('InitiateCheckout', {
      skuCode: selectedSKU.code,
      name: selectedSKU.name,
      valueMxn: mxnValueFromQuote,
    });
    try {
      const checkoutResult = await api.createServiceCheckout(selectedSKU.code, quantity, selectedFlags);
      if (checkoutResult.success && checkoutResult.data?.url) {
        console.log('[CreatePage] Redirecting to Stripe checkout:', checkoutResult.data.url);
        window.location.href = checkoutResult.data.url;
      } else {
        throw new Error(checkoutResult.error || 'No checkout URL returned');
      }
    } catch (error) {
      console.error('[CreatePage] Checkout error:', error);
      trackEvent('checkout_failed', {
        source: 'create_page',
        skuCode: selectedSKU.code,
        reason: error instanceof Error ? error.message : 'unknown_error',
      });
      toast({
        title: t('createPage.checkoutFailed'),
        description: error instanceof Error ? error.message : t('createPage.checkoutFailedBody'),
        variant: 'destructive',
      });
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top Bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-xl border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold font-display">
              <span className="gradient-text">Chop</span>Shop
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <Link to="/chopshop">
              <Button variant="ghost" size="sm">
                {t('createPage.chopShop')}
              </Button>
            </Link>

            <button
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              aria-label={t('createPage.logout')}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <h1 className="text-3xl font-bold mb-2">{t('createPage.title')}</h1>
              <p className="text-muted-foreground">{t('createPage.subtitle')}</p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Panel - Categories and SKUs */}
              <div className="lg:col-span-2 space-y-6">
                {/* Category Selection */}
                <div className="glass-card p-6">
                  <h2 className="text-lg font-semibold mb-4">{t('createPage.serviceCategory')}</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {vectorCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`p-4 rounded-xl border-2 transition-all text-center ${selectedCategory === cat.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/30'
                          }`}
                      >
                        <div className="font-semibold">{cat.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">{cat.code}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* SKU Selection */}
                <div className="glass-card p-6">
                  <h2 className="text-lg font-semibold mb-4">{t('createPage.selectService')}</h2>

                  {isLoadingSKUs ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : skus.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">{t('createPage.noServices')}</div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {skus.map((sku) => (
                        <button
                          key={sku.id}
                          onClick={() => setSelectedSKU(sku)}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${selectedSKU?.id === sku.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/30'
                            }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold mb-1">{sku.name}</div>
                              <div className="text-sm text-muted-foreground mb-2">
                                {sku.description}
                              </div>
                              <div className="flex items-center gap-4 text-xs">
                                <span className="text-muted-foreground">
                                  {t('createPage.creditsMinutes', { credits: sku.baseCredits, minutes: Math.floor(sku.baseCredits / 60) })}
                                </span>
                                <span className="text-primary font-medium">
                                  {displayCredits(sku.baseCredits)}
                                </span>
                              </div>
                            </div>
                            {selectedSKU?.id === sku.id && (
                              <Check className="w-5 h-5 text-primary flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quantity Selection */}
                {selectedSKU && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card p-6"
                  >
                    <h2 className="text-lg font-semibold mb-4">{t('createPage.quantity')}</h2>
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleQuantityChange(-1)}
                        disabled={quantity <= 1}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>

                      <div className="flex-1 text-center">
                        <div className="text-3xl font-bold">{quantity}</div>
                        <div className="text-sm text-muted-foreground">{t('createPage.units')}</div>
                      </div>

                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleQuantityChange(1)}
                        disabled={quantity >= 100}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>

                    {quantity >= 50 && (
                      <div className="mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                        <p className="text-sm text-green-400 flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          {t('createPage.bulkDiscount')}
                        </p>
                      </div>
                    )}
                    {quantity >= 10 && quantity < 50 && (
                      <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <p className="text-sm text-blue-400 flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          {t('createPage.batchDiscount')}
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Customization Options */}
                {selectedSKU && !isLoadingFlags && flags.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card p-6"
                  >
                    <h2 className="text-lg font-semibold mb-4">{t('createPage.customization')}</h2>
                    <p className="text-sm text-muted-foreground mb-4">{t('createPage.customizationNote')}</p>

                    <div className="space-y-3">
                      {flags
                        .filter(flag => {
                          // Filter out default flags and batch flag (auto-applied)
                          if (flag.code === 'B') return false;
                          if (selectedSKU.defaultFlags.includes(flag.code)) return false;
                          return true;
                        })
                        .map((flag) => (
                          <div
                            key={flag.id}
                            className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${selectedFlags.includes(flag.code)
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:border-primary/30'
                              }`}
                            onClick={() => toggleFlag(flag.code)}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={selectedFlags.includes(flag.code)}
                                onCheckedChange={() => toggleFlag(flag.code)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  {getFlagIcon(flag.code)}
                                  <span className="font-semibold">{flag.label}</span>
                                  {flag.priceMultiplier !== 1.0 && (
                                    <span className="text-xs text-primary font-medium">
                                      ×{flag.priceMultiplier}
                                    </span>
                                  )}
                                  {flag.priceAddFlatCents > 0 && (
                                    <span className="text-xs text-primary font-medium">
                                      +{displayCredits(flag.priceAddFlatCents)}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {flag.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>

                    {selectedSKU.defaultFlags.length > 0 && (
                      <div className="mt-4 p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-muted-foreground">
                          <strong>{t('createPage.included')}</strong> {selectedSKU.defaultFlags.map(code => {
                            const flag = flags.find(f => f.code === code);
                            return flag?.label || code;
                          }).join(', ')}
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              {/* Right Panel - Quote Summary */}
              <div className="space-y-6">
                <div className="glass-card p-6 sticky top-24">
                  <h2 className="text-lg font-semibold mb-4">{t('createPage.orderSummary')}</h2>

                  {!selectedSKU ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">{t('createPage.selectToSeePricing')}</div>
                  ) : isLoadingQuote ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : quote ? (
                    <div className="space-y-4">
                      <div>
                        <div className="font-medium mb-1">{quote.skuName}</div>
                        <div className="text-sm text-muted-foreground">
                          {t('createPage.quantityLabel', { count: quote.quantity })}
                        </div>
                        {quote.appliedFlags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {quote.appliedFlags.map(flagCode => {
                              const flag = flags.find(f => f.code === flagCode);
                              return flag ? (
                                <span key={flagCode} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/10 text-primary text-xs">
                                  {getFlagIcon(flagCode)}
                                  {flag.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2 pt-4 border-t border-border">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{t('createPage.basePrice')}</span>
                          <span>{displayCredits(quote.customerPriceCents)}</span>
                        </div>

                        {quote.overageSeconds > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              {t('createPage.overage', { minutes: Math.floor(quote.overageSeconds / 60) })}
                            </span>
                            <span className="text-orange-400">{displayCredits(quote.overageCostCents)}</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{t('createPage.processingTime')}</span>
                          <span>{t('createPage.minutes', { count: Math.floor(quote.totalSeconds / 60) })}</span>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">{t('createPage.totalPrice')}</span>
                          <span className="text-2xl font-bold text-primary">
                            {displayCredits(quote.customerPriceCents)}
                          </span>
                        </div>

                        {quote.secondsFromPlan > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {t('createPage.fromPlan', { minutes: Math.floor(quote.secondsFromPlan / 60) })}
                          </div>
                        )}
                      </div>

                      {quote.overageSeconds > 0 && (
                        <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-orange-400">{t('createPage.overageWarning')}</p>
                          </div>
                        </div>
                      )}

                      <div className="pt-4">
                        <div className="text-xs text-muted-foreground">
                          {t('createPage.margin', { percent: quote.marginPercent })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t('createPage.planRemaining', { minutes: Math.floor(quote.remainingPlanSeconds / 60) })}
                        </div>
                      </div>

                      <Button
                        variant="hero"
                        size="lg"
                        className="w-full"
                        onClick={handlePlaceOrder}
                        disabled={isPlacingOrder}
                      >
                        {isPlacingOrder ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            {t('createPage.redirecting')}
                          </>
                        ) : (
                          <>
                            <ShoppingCart className="w-5 h-5 mr-2" />
                            {t('createPage.checkoutStripe')}
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-destructive text-sm">{t('createPage.unableToPrice')}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
